/**
 * The workspace's own sheet (WEB-10886): what Tailwind cannot express and what
 * the hosted V1 views need to look like the design.
 *
 * Three kinds of rule live here, each scoped to a class the workspace sets:
 *
 * - `.pw-root`: the design's tokens the Tailwind config has no name for, and the
 *   design's monospace face.
 * - `.pw-chat`: the shared chat components (MessageBlock, ChatInput) are styled
 *   by the praxis sheet for the AI Workspace; inside this pane they keep that
 *   thread and take the design's composer.
 * - `.pw-dossier`, `.pw-run`, `.pw-plan`: the V1 dossier, run detail and plan
 *   review keep their markup and their `dx-` / `rh-` / `pa-` / `ps-` / `ep-`
 *   classes; these rules restate the design's values for those classes. The V1
 *   screen itself never carries these wrappers, so it is untouched.
 *
 * Every value is the design's (repos/designs/pav2a.html); nothing here is a
 * choice of its own.
 */
import dossierDrawerStyles from '../ProspectingAgent/dossierDrawerStyles';

const workspaceStyles = `
@import url('https://fonts.googleapis.com/css2?family=Roboto+Mono:wght@400;500&display=swap');

.pw-root {
  --pw-green-1: #e8f9f8;
  --pw-green-3: #41909b;
  --pw-green-4: #086471;
  --pw-success-2: #c8e6c9;
  --pw-success-5: #388e3c;
  --pw-info-2: #a3c6f5;
  --pw-info-4: #1f5cad;
  --pw-alert-3: #fad668;
  --pw-danger-2: #fddccc;
  --pw-beta-2: #e6dffc;
  --pw-beta-4: #a08fc3;
  --pw-beta-5: #6c528c;
  --pw-rose-5: #86476b;
  --pw-mono: 'Roboto Mono', ui-monospace, SFMono-Regular, Menlo, monospace;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
  /* The design inherits the body's 1.5; the shadow root has no html/body to
     inherit from, so the root class supplies it. */
  line-height: 1.5;
}
.pw-root .font-mono { font-family: var(--pw-mono); }

@keyframes pw-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: .45; transform: scale(.78); }
}
.pw-pulse { animation: pw-pulse 1.6s ease-in-out infinite; }

/* The Search's panes sliding as the results open (WEB-13230, canvas G): the chat and the
   filters give up width while the results grow and fade in, a beat behind the slide. Only
   flex, margin and opacity move, so nothing reflows mid-turn. */
.pw-slide {
  transition: flex .65s cubic-bezier(.2, .7, .2, 1), margin .65s cubic-bezier(.2, .7, .2, 1), opacity .35s ease;
}
.pw-slide.pw-slide-in { transition-delay: 0s, 0s, .25s; }
/* A filter the assistant's turn just set, flashing once as it lands (WEB-13232). */
@keyframes pw-flash {
  0%, 30% { background-color: var(--pw-beta-2); box-shadow: 0 0 0 2px var(--pw-beta-4); }
  100% { box-shadow: 0 0 0 0 transparent; }
}
[data-flash] { animation: pw-flash 1.6s ease-out; }
@media (prefers-reduced-motion: reduce) {
  .pw-slide { transition: none; }
  [data-flash] { animation: none; }
}

.pw-skeleton {
  color: transparent !important;
  border-radius: 6px;
  background: linear-gradient(90deg, #edf1f5 25%, #f6f9fb 50%, #edf1f5 75%);
  background-size: 200% 100%;
  animation: pw-shimmer 1.4s ease-in-out infinite;
}

/* ── Chat pane ─────────────────────────────────────────────────────────── */
/* The thread itself - bubbles, the reply's header with who and when, its text - is
   the AI Workspace's, untouched, so the two chats read the same (WEB-15681). */
/* The sticky day divider takes the pane's own white, so scrolled content
   passes behind it instead of showing through a mismatched band. */
.pw-chat .pr-date-divider, .pw-chat .pr-date-divider::before { background: #fff; }
/* The pane already insets the thread by 14px; MessageThread's own 20px sides would double it. */
.pw-chat .pw-thread-host > div { padding-left: 0; padding-right: 0; }
.pw-chat .pr-composer-box.compact {
  height: auto; min-height: 38px; gap: 8px;
  padding: 0 10px;
  border: 1px solid #d3dae4; border-radius: 6px; background: #fff;
}
.pw-chat .pr-composer-box.compact.focused { border-color: #1d3d48; box-shadow: none; }
.pw-chat .pr-composer-box.compact .pr-composer-textarea { font-size: 12px; color: #3e4957; }
.pw-chat .pr-composer-textarea::placeholder { color: #9baabe; }
.pw-chat .pr-composer-tool { color: #9baabe; }
.pw-chat .pr-send-btn { width: 20px; height: 20px; border-radius: 4px; background: #1d3d48; color: #fff; }
.pw-chat .pr-send-btn svg { width: 11px; height: 11px; }

/* The slide-over and the V1 dossier inside it are shared with the V1 screen
   (WEB-10892), so their rules live in dossierDrawerStyles.ts. */
${dossierDrawerStyles}
/* ── The expanded run in History ───────────────────────────────────────── */
.pw-run .rh-detail { border: none; border-radius: 0; background: transparent; overflow: visible; }
.pw-run .rh-detail-head { display: none; }
.pw-run .rh-stats { display: flex; gap: 0; background: #fff; border-top: 1px solid #e9eef3; }
.pw-run .rh-stat {
  flex: 1; display: flex; flex-direction: column-reverse;
  padding: 9px 14px; border-right: 1px solid #e9eef3;
}
.pw-run .rh-stat:last-child { border-right: none; }
.pw-run .rh-stat-v { font-family: var(--pw-mono); font-size: 16px; font-weight: 400; color: #2f3947; letter-spacing: 0; }
.pw-run .rh-stat-l { font-family: var(--pw-mono); font-size: 10px; font-weight: 400; color: #6b7c93; margin: 0; }

/* flex-direction resets the V1 sheet's column: in a column flex the 0 0 100%
   children go shrink-to-fit wide, which squeezed the table to half the card. */
.pw-run .rh-detail-body { display: flex; flex-direction: row; flex-wrap: wrap; align-items: flex-start; gap: 0; padding: 0; }
.pw-run .rh-detail-body > * { order: 3; flex: 0 0 100%; border-top: 1px solid #e9eef3; }
.pw-run .rh-query-card {
  order: 1; flex: 1 1 calc(50% - 7px); min-width: 0;
  margin: 12px 0 0 14px; padding: 9px 12px;
  border-top: none; border-left: 3px solid #d3dae4; background: #f6f9fb; border-radius: 0 6px 6px 0;
}
.pw-run .rh-query-label { font-family: var(--pw-mono); font-size: 10px; font-weight: 400; letter-spacing: .07em; color: #6b7c93; margin-bottom: 3px; }
.pw-run .rh-query-body { font-family: inherit; font-size: 12px; font-style: normal; line-height: 1.5; color: #3e4957; }
.pw-run .rh-detail-body > div:has(> .rh-summary) {
  order: 2; flex: 1 1 calc(50% - 7px); min-width: 0;
  margin: 12px 14px 0; padding: 9px 12px;
  border-top: none; border-left: 3px solid var(--pw-beta-5); background: #f2effd; border-radius: 0 6px 6px 0;
}
.pw-run .rh-detail-body > div:has(> .rh-summary) .dx-section-h { margin-bottom: 3px; }
.pw-run .rh-detail-body > div:has(> .rh-summary) .dx-section-label {
  font-family: var(--pw-mono); font-size: 10px; font-weight: 400; letter-spacing: .07em; color: var(--pw-beta-5);
}
.pw-run .rh-summary { padding: 0; border: none; background: none; border-radius: 0; }
.pw-run .rh-summary-body { font-size: 12px; line-height: 1.5; color: #3e4957; }
.pw-run .rh-summary-toggle { font-size: 11px; color: #1d3d48; margin-top: 4px; }
.pw-run .rh-detail-body > .ps-card { margin-top: 12px; }

/* The pipeline rail: ✓ mint, pulsing purple, hollow grey, joined by a 2px line. */
.pw-run .ps-card { border: none; border-radius: 0; background: #fff; overflow: visible; }
.pw-run .ps-rail { padding: 12px 14px 14px; background: #fff; border-bottom: none; }
.pw-run .ps-rail-title {
  display: block; margin-bottom: 10px;
  font-family: var(--pw-mono); font-size: 10px; letter-spacing: .07em;
  text-transform: uppercase; color: #6b7c93;
}
.pw-run .ps-rail-track { display: none; }
.pw-run .ps-rail-nodes { gap: 0; }
.pw-run .ps-rail-node { position: relative; gap: 6px; padding: 0; cursor: default; }
.pw-run .ps-rail-node:not(:first-child)::before {
  content: ''; position: absolute; top: 8px; right: 50%; width: 100%; height: 2px; background: #e4eaf0;
}
.pw-run .ps-rail-done::before, .pw-run .ps-rail-running::before { background: #c3e8e8; }
.pw-run .ps-rail-running::before { background: linear-gradient(to right, #c3e8e8, var(--pw-beta-4)); }
.pw-run .ps-rail-node-dot {
  position: relative; z-index: 1;
  width: 18px; height: 18px; font-size: 10px; font-weight: 400;
  border: 1.5px solid #cad2dd; background: #fff; color: #1d3d48; font-family: inherit;
}
.pw-run .ps-rail-done .ps-rail-node-dot { background: #c3e8e8; border-color: #c3e8e8; color: #1d3d48; }
.pw-run .ps-rail-running .ps-rail-node-dot {
  background: var(--pw-beta-5); border-color: var(--pw-beta-5); color: #fff;
  box-shadow: 0 0 0 4px var(--pw-beta-2); animation: pw-pulse 1.6s ease-in-out infinite;
}
.pw-run .ps-rail-running .ps-pulse-sm { width: 5px; height: 5px; background: #fff; animation: none; }
.pw-run .ps-rail-failed .ps-rail-node-dot { background: #b51601; border-color: #b51601; color: #fff; }
.pw-run .ps-rail-skipped .ps-rail-node-dot { background: #fff; border-style: dashed; border-color: #cad2dd; color: #9baabe; }
.pw-run .ps-rail-label {
  font-size: 11px; font-weight: 400; letter-spacing: 0; text-transform: none; white-space: normal;
  color: #9baabe; line-height: 1.3;
}
.pw-run .ps-rail-done .ps-rail-label { color: #4b5562; }
.pw-run .ps-rail-running .ps-rail-label { color: #3a0e55; font-weight: 500; }
.pw-run .ps-rail-failed .ps-rail-label { color: #7a1100; }
.pw-run .ps-rail-time { display: none; }
.pw-run .ps-stages-toggle { padding: 0 14px; border-top-color: #e9eef3; }
.pw-run .ps-stages-toggle-btn { font-size: 11px; color: #6b7c93; padding: 8px 0; }

/* The scored candidate table. */
.pw-run .rh-detail-body > div:has(> .pa-table) { border-top: 1px solid #e4eaf0; }
.pw-run .rh-detail-body > div:has(> .pa-table) > .dx-section-h { display: none; }
.pw-run .pa-table { display: block; }
.pw-run .pa-table thead, .pw-run .pa-table tbody { display: block; }
.pw-run .pa-table tr {
  display: grid; grid-template-columns: 34px 62px minmax(0, 1fr) 260px 190px;
  align-items: center; padding: 0 14px; min-height: 52px;
  border-bottom: 1px solid #e9eef3;
}
.pw-run .pa-table thead tr {
  min-height: 34px; background: #edf1f5; border-bottom: 1px solid #e4eaf0;
}
.pw-run .pa-table th {
  padding: 0; text-align: left;
  font-family: var(--pw-mono); font-size: 10px; font-weight: 400; letter-spacing: .07em;
  text-transform: uppercase; color: #6b7c93;
}
.pw-run .pa-table td { padding: 8px 0; vertical-align: middle; }
.pw-run .pa-table th:nth-child(1), .pw-run .pa-table td:nth-child(1) { order: 1; width: auto; padding-right: 0 !important; }
.pw-run .pa-table th:nth-child(5), .pw-run .pa-table td:nth-child(5) { order: 2; width: auto; text-align: left; }
.pw-run .pa-table th:nth-child(2), .pw-run .pa-table td:nth-child(2) { order: 3; min-width: 0; }
.pw-run .pa-table th:nth-child(3), .pw-run .pa-table td:nth-child(3) { order: 4; }
.pw-run .pa-table th:nth-child(4), .pw-run .pa-table td:nth-child(4) { order: 5; }
.pw-run .pa-table tbody tr { cursor: pointer; }
.pw-run .pa-table tbody tr:hover, .pw-run .pa-table tbody tr.on { background: #f6f9fb; }
.pw-run .pa-table input[type=checkbox] { width: 13px; height: 13px; margin: 0; accent-color: #1d3d48; }
.pw-run .pa-score {
  min-width: 0; height: auto; padding: 2px 9px; border-radius: 999px;
  font-family: var(--pw-mono); font-size: 12px; font-weight: 500; border: 1px solid transparent;
}
.pw-run .pa-score-success { background: #e8f5e9; border-color: var(--pw-success-2); color: #1b5e20; }
.pw-run .pa-score-warn { background: #fef9e7; border-color: var(--pw-alert-3); color: #664c00; }
.pw-run .pa-score-muted { background: #edf1f5; border-color: #d3dae4; color: #4b5562; }
.pw-run .pa-table td:nth-child(2) { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.pw-run .pa-company-name { font-size: 13px; font-weight: 500; color: #000; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pw-run .pa-table td:nth-child(2) > .pa-meta { display: none; }
.pw-run .pa-crm-actions { margin-left: 0; gap: 6px; flex-wrap: wrap; }
.pw-run .pa-existing-badge, .pw-run .pa-imported-badge {
  margin-left: 0; padding: 1px 5px; border-radius: 3px; gap: 0;
  background: #c3e8e8; color: #1d3d48; font-size: 10px; font-weight: 400;
}
.pw-run .pa-imported-badge svg { display: none; }
.pw-run .pa-match-tag { display: none; }
.pw-run .pa-sync-btn {
  padding: 3px 9px; border-radius: 4px; gap: 0;
  border: 1px solid #d3dae4; background: #fff; color: #1d3d48; font-size: 11px; font-weight: 400;
}
.pw-run .pa-sync-btn:hover { background: #f6f9fb; }
.pw-run .pa-sync-btn svg { display: none; }
.pw-run .pa-sync-btn--new { color: #1d3d48; }
.pw-run .pa-tags { gap: 5px; }
.pw-run .pa-signal-tag {
  padding: 1px 6px; border-radius: 3px; font-size: 10px; font-weight: 400; line-height: 1.5;
  background: #e4edf7; color: #002c66;
}
.pw-run .pa-signal-tag--high { background: #e8f5e9; color: #1b5e20; }
.pw-run .pa-signal-tag--medium { background: #e4edf7; color: #002c66; }
.pw-run .pa-signal-tag--warn { background: #fef9e7; color: #664c00; }
.pw-run .pa-contact-name { font-size: 12px; font-weight: 400; color: #3e4957; }
.pw-run .pa-table td:nth-child(4) .pa-meta { font-family: var(--pw-mono); font-size: 10px; color: #9baabe; margin-top: 0; }
.pw-run .pa-table td:nth-child(3) .pa-meta, .pw-run .pa-table td:nth-child(4) .pa-meta[style] { font-family: inherit; font-size: 12px; color: #9baabe; }
.pw-run .pa-explore-selection-bar {
  position: static; margin: 0; padding: 11px 14px; border-radius: 0; box-shadow: none; animation: none;
  background: #f6f9fb; color: #6b7c93; gap: 8px; justify-content: flex-start;
}
.pw-run .pa-explore-selection-count { font-family: var(--pw-mono); font-size: 11px; color: #6b7c93; margin-right: auto; }
.pw-run .pa-explore-selection-count strong { font-weight: 400; }
.pw-run .pa-explore-selection-bar .pa-btn {
  padding: 6px 12px; border-radius: 4px; gap: 6px; font-size: 12px; font-weight: 400; box-shadow: none;
  background: #1d3d48; color: #fff;
}
.pw-run .pa-explore-selection-bar .pa-btn--primary { background: #c3e8e8; color: #1d3d48; }

/* ── The plan review (right pane of Engagement plans) ─────────────────── */
.pw-plan .ep { max-width: none; margin: 0; padding: 0 0 14px; display: flex; flex-direction: column; flex: 1; }
.pw-plan .ep > .dx-name { display: none; }
.pw-plan .ep-company {
  display: block; margin: 0; padding: 12px 16px; border-bottom: 1px solid #e9eef3;
  font-size: 15px; font-weight: 600; color: #000;
}
.pw-plan .ep-company:hover { text-decoration: none; color: #1d3d48; }
.pw-plan .ep-summary { margin: 0; padding: 12px 16px; border-bottom: 1px solid #e9eef3; font-size: 12px; line-height: 1.5; color: #4b5562; }
.pw-plan .ep-contact { margin: 14px 16px 0; border: 1px solid #e4eaf0; border-radius: 8px; }
.pw-plan .ep-contact + .ep-contact { margin-top: 12px; }
.pw-plan .ep-contact-h { align-items: center; gap: 10px; padding: 10px 12px; background: #f6f9fb; border-bottom: 1px solid #e9eef3; }
.pw-plan .ep-contact-role { font-size: 13px; font-weight: 500; color: #2f3947; }
.pw-plan .ep-contact-angle { font-size: 11px; color: #6b7c93; }
.pw-plan .ep-contact-priority { font-family: var(--pw-mono); font-size: 11px; color: #6b7c93; }
.pw-plan .ep-steps { padding: 0; }
.pw-plan .ep-step {
  display: grid; grid-template-columns: 64px minmax(0, 1fr) auto; column-gap: 12px;
  padding: 11px 12px; border-bottom: 1px solid #e9eef3;
}
.pw-plan .ep-step:last-child { border-bottom: none; }
.pw-plan .ep-step-h { grid-column: 1; grid-row: 1 / span 4; flex-direction: column; align-items: center; gap: 5px; margin: 0; }
.pw-plan .ep-day { min-width: 0; font-family: var(--pw-mono); font-size: 11px; color: #2f3947; }
.pw-plan .ep-pill { padding: 1px 8px; gap: 0; border: none; background: #c3e8e8; color: #1d3d48; font-size: 10px; font-weight: 400; }
.pw-plan .ep-pill svg { display: none; }
/* The design colours the channel pills: Email keeps the mint default,
   LinkedIn is info, Call (and todo) grey. */
.pw-plan .ep-pill--linkedin { background: #e4edf7; color: #002c66; }
.pw-plan .ep-pill--call, .pw-plan .ep-pill--todo { background: #edf1f5; color: #6b7c93; }
.pw-plan .ep-status { margin: 0; padding: 1px 6px; border-radius: 3px; font-size: 10px; font-weight: 400; letter-spacing: 0; text-transform: none; }
.pw-plan .ep-status-approved { background: #e4edf7; color: #002c66; }
.pw-plan .ep-status-sent { background: #e8f5e9; color: #1b5e20; }
.pw-plan .ep-status-completed { background: #edf1f5; color: #3e4957; }
.pw-plan .ep-ref { background: #edf1f5; border: none; border-radius: 4px; padding: 3px 9px; font-size: 11px; color: #3e4957; }
.pw-plan .ep-status-draft { display: none; }
.pw-plan .ep-subject, .pw-plan .ep-body, .pw-plan .ep-refs, .pw-plan .ep-edit { grid-column: 2; }
.pw-plan .ep-subject { margin: 0 0 3px; font-size: 12px; font-weight: 500; color: #2f3947; }
.pw-plan .ep-body { font-size: 12px; line-height: 1.55; color: #4b5562; }
.pw-plan .ep-step-tools { grid-column: 3; grid-row: 1 / span 4; flex-direction: column; gap: 5px; margin: 0; }
.pw-plan .ep-link {
  padding: 3px 9px; border: 1px solid #d3dae4; border-radius: 4px; text-align: center; text-decoration: none;
  font-size: 11px; color: #1d3d48; background: #fff;
}
.pw-plan .ep-link:nth-child(2) { color: #6b7c93; }
.pw-plan .ep-link:hover { background: #f6f9fb; }
.pw-plan .ep-approve-bar {
  margin: auto 0 0; padding: 11px 16px; gap: 12px;
  border-top: 1px solid #e9eef3; background: #f6f9fb;
}
.pw-plan .ep-contact:last-of-type { margin-bottom: 14px; }
.pw-plan .ep-selectall { font-size: 13px; font-weight: 500; color: #2f3947; gap: 12px; }
.pw-plan .ep-check { width: 15px; height: 15px; accent-color: #1d3d48; }
.pw-plan .ep-approve-bar .dx-btn { margin-left: auto; }
.pw-plan .dx-btn { padding: 6px 12px; border-radius: 4px; gap: 6px; border: 1px solid #d3dae4; background: #fff; color: #1d3d48; font-size: 12px; font-weight: 400; }
.pw-plan .dx-btn svg { display: none; }
.pw-plan .dx-btn--primary { background: #1d3d48; border-color: #1d3d48; color: #fff; }
.pw-plan .ep > div[style] { padding: 0 16px; }
.pw-plan .ep-err { padding: 0 16px; }
.pw-plan .ep-generating, .pw-plan .ep-gen-panel { margin: 14px 16px; }
.pw-plan .ep-gen-panel .dx-btn { align-self: flex-end; }
.pw-plan .ep-gen-panel { max-width: none; border-color: #e4eaf0; border-radius: 8px; box-shadow: none; }
.pw-plan .dx-empty { padding: 14px 16px; font-size: 12px; color: #6b7c93; }
`;

export default workspaceStyles;
